<template>
    <div class="add-bank-form-container">
        <p class="title">Add Bank</p>
        <div v-if="showTestMsg" class="testing-msg">
            <p class="msg">For Testers only:</p>
            <div class="test-bank-info">
                <div class="test-routing-info">
                    <p class="label">ROUTING</p>
                    <p class="value">110000000</p>
                </div>
                <div class="divider"></div>
                <div class="test-acct-info">
                    <p class="label">ACCOUNT</p>
                    <p class="value">000999999991</p>
                </div>
            </div>
        </div>
        <div v-for="(input, indx) in formInputs" :key="input.label" :class="input.wrapper">
            <label :class="inputErr(input, indx) ? 'error-msg' : ''" :for="input.name">
                {{ input.label }}
            </label>
            <input
                v-if="input.type === 'input'"
                v-model="$v.formInputs.$each[indx].value.$model"
                @blur="$v.formInputs.$each[indx].value.$touch"
                :style="inputErr(input, indx) ? { 'border-color': '#ed340d' } : {}"
                :placeholder="input.placeholder"
                type="text"
            />
            <div
                v-else
                @click="openAcctTypeDropdown"
                @keydown.enter="openAcctTypeDropdown"
                @keydown.esc="closeAcctTypeDropdown"
                class="acct-type"
                tabindex="0"
            >
                <div
                    v-if="!input.value"
                    :class="`${input.wrapper}-placeholder`"
                    :style="inputErr(input, indx) ? { 'border-color': '#ed340d' } : {}"
                >
                    <p class="placeholder">
                        {{ input.placeholder }}
                    </p>
                    <img
                        :src="dropdownOpen ? URLS.icons.vectorUp : URLS.icons.vectorDown"
                        class="dropdown-icon"
                        alt="vector"
                    />
                </div>
                <div v-else :class="`${input.wrapper}-value`">
                    <p class="value">
                        {{ formatInputValue(input) }}
                    </p>
                    <img
                        :src="dropdownOpen ? URLS.icons.vectorUp : URLS.icons.vectorDown"
                        class="dropdown-icon"
                        alt="vector"
                    />
                </div>
            </div>
            <div
                ref="dropdown"
                v-if="dropdownOpen && input.name === 'account_holder_type'"
                class="type-options"
                tabindex="0"
            >
                <p
                    v-for="(type, indx) in acctTypes"
                    :key="indx"
                    @click="selectType(type)"
                    @keyup.enter="selectType(type)"
                    @keyup.esc="closeAcctTypeDropdown"
                    class="type-option"
                    tabindex="0"
                >
                    {{ type.text }}
                </p>
            </div>
            <p v-if="inputErr(input, indx)" class="error-msg">{{ input.errMsg }}</p>
        </div>
        <button @click="addBank" class="cta">Add Bank</button>
    </div>
</template>
<script>
    import { usePopup } from '@/composables/ui/usePopup'
    import { required, numeric } from 'vuelidate/lib/validators'

    const URLS = {
        icons: {
            vectorUp: '/images/icons/vector-up.svg',
            vectorDown: '/images/icons/vector-down.svg'
        }
    }
    export default {
        name: 'AddBankForm',
        emit: ['add-bank-info'],
        setup() {
            const { popup: dropdown, popupOpen: dropdownOpen } = usePopup()

            return {
                dropdown,
                dropdownOpen
            }
        },
        validations: {
            formInputs: {
                $each: {
                    value: {
                        required,
                        numeric
                    }
                }
            }
        },
        data: () => ({
            acctTypes: [
                {
                    text: 'Individual',
                    value: 'individual'
                },
                {
                    text: 'Corporate',
                    value: 'company'
                }
            ],
            formInputs: [
                {
                    name: 'account_holder_name',
                    wrapper: 'full-name-input',
                    type: 'input',
                    label: 'Full Legal Name',
                    placeholder: 'Add Name',
                    value: '',
                    errMsg: 'Field is required.'
                },
                {
                    name: 'account_holder_type',
                    wrapper: 'acct-type-input',
                    type: 'div',
                    label: 'Account Holder Type',
                    placeholder: 'Account Type',
                    value: '',
                    errMsg: 'Field is required.'
                },
                {
                    name: 'routing_number',
                    wrapper: 'routing-num-input',
                    type: 'input',
                    label: 'Routing Number',
                    placeholder: 'Add Routing Number',
                    value: '',
                    errMsg: 'Field is required. Please enter a 9 digit number.'
                },
                {
                    name: 'account_number',
                    wrapper: 'acct-num-input',
                    type: 'input',
                    label: 'Account Number',
                    placeholder: 'Add Account Number',
                    value: '',
                    errMsg: 'Field is required. Please enter numbers only.'
                }
            ]
        }),
        computed: {
            URLS: () => URLS,
            showTestMsg: () => window.location.hostname !== 'hedlyner.com',
            inputErr: (self) => (_input, indx) => {
                switch (_input.name) {
                    case 'account_holder_name':
                        return self.requiredError(indx)
                    case 'routing_number':
                        return (
                            self.requiredError(indx) ||
                            self.numError(indx) ||
                            self.routingNumLengthError(indx)
                        )
                    case 'account_number':
                        return (
                            self.requiredError(indx) ||
                            self.numError(indx) ||
                            self.acctNumLengthError(indx)
                        )
                    case 'account_holder_type':
                        return self.acctTypeError
                    default:
                        return true
                }
            },
            requiredError: (self) => (indx) =>
                self.$v.formInputs.$each[indx].value.$dirty &&
                !self.$v.formInputs.$each[indx].value.required,
            numError: (self) => (indx) =>
                self.$v.formInputs.$each[indx].value.$dirty &&
                !self.$v.formInputs.$each[indx].value.numeric,
            acctTypeError: (self) =>
                self.formInputs.find((i) => i.name === 'account_holder_name').value &&
                self.formInputs.find((i) => i.name === 'routing_number').value &&
                self.formInputs.find((i) => i.name === 'account_number').value &&
                !self.formInputs.find((i) => i.name === 'account_holder_type').value,
            routingNumLengthError: (self) => (indx) =>
                self.$v.formInputs.$each[indx].value.$dirty &&
                self.formInputs.find((i) => i.name === 'routing_number').value.length != 9,
            acctNumLengthError: (self) => (indx) =>
                self.$v.formInputs.$each[indx].value.$dirty &&
                (self.formInputs.find((i) => i.name === 'account_number').value.length < 8 ||
                    self.formInputs.find((i) => i.name === 'account_number').value.length >
                        17),

            formatInputValue: (self) => (input) => {
                if (input.name !== 'account_holder_type') return input.value

                return self.getAcctHolderTypeText(input.value)
            },
            getAcctHolderTypeText: (self) => (val) =>
                self.acctTypes.find((t) => t.value === val).text
        },
        methods: {
            openAcctTypeDropdown() {
                this.dropdownOpen = true
            },
            closeAcctTypeDropdown() {
                this.dropdownOpen = false
            },
            selectType(type) {
                let acctTypeInput = this.formInputs.find(
                    (i) => i.name === 'account_holder_type'
                )

                if (acctTypeInput) {
                    acctTypeInput.value = type.value
                }

                this.closeAcctTypeDropdown()
            },
            validateInputs() {
                let valid = true

                this.$v.$touch()
                this.formInputs.forEach((_input, indx) => {
                    if (this.inputErr(_input, indx)) valid = false
                })

                return valid
            },
            prepData() {
                let data
                this.formInputs.forEach((_input) => {
                    data = {
                        ...data,
                        [_input.name]: _input.value
                    }
                })

                return data
            },
            addBank() {
                if (this.validateInputs()) {
                    const data = this.prepData()
                    this.$emit('add-bank-info', data)
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    $mobileMax: 600px;
    @media screen and (max-width: $mobileMax) {
        .add-bank-form-container {
            .full-name-input,
            .routing-num-input,
            .acct-num-input,
            .cta,
            .testing-msg,
            .acct-type-input-placeholder,
            .acct-type-input-value {
                width: 100% !important;
            }
        }
    }
    .add-bank-form-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 24px;
        width: 100%;

        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        label {
            color: #8b8b8b;
            font-family: Instrument Sans;
            font-size: 12px;
            font-style: normal;
            font-weight: 650;
            line-height: 135%;
            letter-spacing: 0.12px;
        }
        input {
            width: 100%;
            padding: 12px 16px 16px 16px;
            border-radius: 12px;
            border: 1px solid #8b8b8b;
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 16px;
            font-style: normal;
            font-weight: 550;
            line-height: 125%;
            background: inherit;

            input::placeholder {
                overflow: hidden;
                color: #646464;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }
        }
        .error-msg {
            color: #ed3c0d !important;
            font-family: Instrument Sans;
            font-size: 12px;
            font-style: normal;
            font-weight: 550;
            line-height: 125%;
        }
        .title {
            margin-top: 4px;
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 32px;
            font-style: normal;
            font-weight: 500;
            line-height: 120%;
            letter-spacing: -0.64px;
        }
        .testing-msg {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border-radius: 15px;
            gap: 20px;
            background: #262626;
            width: 60%;
            padding: 16px;

            .msg {
                color: #dedede;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.64px;
            }
            .test-bank-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 10px;
                align-self: stretch;
                border: 1px solid #8b8b8b;
                border-radius: 35px;
                background: #cdeb09;

                .divider {
                    width: 100%;
                    height: 1px;
                    background: #8b8b8b;
                }
                .test-routing-info,
                .test-acct-info {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-around;
                    align-items: center;
                    width: 100%;
                    padding: 10px;

                    .label,
                    .value {
                        color: #121212;
                        font-family: Instrument Sans;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 120%;
                        letter-spacing: -0.64px;
                    }
                }
            }
        }
        .full-name-input,
        .routing-num-input,
        .acct-num-input {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            width: 60%;
            gap: 6px;
        }
        .acct-type-input {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            width: 100%;
            gap: 6px;

            .acct-type:focus {
                outline: none;
            }
            .acct-type {
                width: 100%;

                .acct-type-input-placeholder,
                .acct-type-input-value {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    width: 60%;
                    border-radius: 12px;
                    border: 1px solid #8b8b8b;
                    background: inherit;
                    padding: 12px 16px 16px 16px;

                    p {
                        width: 100%;
                        background: inherit;
                    }
                }
                .acct-type-input-placeholder {
                    .placeholder {
                        overflow: hidden;
                        color: #646464;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                    }
                }
                .acct-type-input-value {
                    .value {
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                    }
                }
            }
            .type-options::-webkit-scrollbar {
                width: 8px;
            }
            .type-options::-webkit-scrollbar-track {
                background-color: none;
            }
            .type-options::-webkit-scrollbar-thumb {
                max-height: 64px;
                border-radius: 8px;
                background: #8b8b8b;
            }
            .type-options {
                position: absolute;
                z-index: 1;
                top: 30%;
                right: 40%;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 16px;
                max-width: 100%;
                max-height: 155px;
                overflow-y: scroll;
                overflow-x: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                padding: 12px 12px 16px 16px;
                border-radius: 7px;
                border: 1px solid #8b8b8b;
                background: #262626;
                .type-option {
                    cursor: pointer;
                    color: #646464;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                    width: 100%;
                }
            }
        }
        .cta {
            border-radius: 100px;
            background: #cdeb09;
            padding: 11px 30px 12px 30px;
            color: #161616;
            font-family: Inter;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
        }
    }
</style>
